import { useMemo } from 'react';
import { useParams } from 'react-router';
import { clsx } from 'clsx';
import Collection from '@model-core/core/Collection';
import { Value } from '@/components/dashboard/DashboardParts';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { FilterSelect } from '@/components/list/FilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import { ExportButton } from '@/components/exports/ExportButton';
import { CollaboratorBreakdown } from '@/pages/dashboard/CollaboratorBreakdown';
import { exportFiltersOf } from '@/components/exports/exports';
import { SearchInput } from '@/components/list/SearchInput';
import { Pagination } from '@/components/table/Pagination';
import { PersonCell } from '@/components/table/PersonCell';
import { RowViewLink } from '@/components/table/RowViewLink';
import { SortableHeader } from '@/components/table/SortableHeader';
import { TeamTag } from '@/components/teams/TeamTag';
import { AbsenceBadge } from '@/components/timesheet/AbsenceBadge';
import { ABSENCE_SIGNATURE_PENDING, SignatureCell } from '@/components/timesheet/SignatureCell';
import { useSignatures } from '@/hooks/useSignatures';
import { useFollowEntityCompany } from '@/hooks/useFollowEntityCompany';
import { useListControls } from '@/hooks/useListControls';
import { useLoader } from '@/hooks/useLoader';
import { usePagedList } from '@/hooks/usePagedList';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import PeriodModel from '@/models/entities/PeriodModel';
import PeriodStatusHistoryModel from '@/models/entities/PeriodStatusHistoryModel';
import TeamModel from '@/models/entities/TeamModel';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { PERIOD_STATUS, TEAM_STATUS, TIME_SHEET_STATUS } from '@/models/references/references';
import { canAccess } from '@/security/navigation';
import { useAccessRoles } from '@/security/useAccessRoles';
import { loadReferences, resolveReference, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import {
  buildPeriodSheetsRequest,
  DEFAULT_PERIOD_SHEET_FILTERS,
  DEFAULT_PERIOD_SHEET_SORT,
} from './historyDetailQuery';
import type { ResolvablePath } from '@/models/resolve';
import { TIME_SHEET_SUMMARIES } from '@/models/entities/TimeSheetSummaryModel';
import { PERIOD_SUMMARIES } from '@/models/entities/PeriodSummaryModel';
import { fetchSummaries } from '@/models/summaries';
import { teamTotalsLoader, type PeriodTotals } from '@/models/entities/PeriodTeamSummaryModel';
import { reviewerOf } from '@/components/timesheet/reviewActions';
import { useCurrentCompany } from '@/stores/useCompanyStore';

/** Collaborateur et équipe des feuilles (la période est celle affichée) */
const RESOLVABLES: ResolvablePath[] = [
  ['member', 'user'],
  ['teamMember', 'team'],
];

const BACK = { to: '/history', label: "Tout l'historique" };

interface PeriodRecap {
  period: PeriodModel;
  /** Ceux de la société (Dirigeant), ou de ses équipes (manager) */
  totals: PeriodTotals;
  /** Ligne Clôturée de l'historique de la période (absente pour une période ouverte) */
  closing: PeriodStatusHistoryModel | null;
}

/** Clé « id|équipes du manager » (sans équipe : le Dirigeant) */
async function loadRecap(key: string): Promise<PeriodRecap> {
  const [id, scope] = key.split('|');
  const teamIds = scope ? scope.split(',').map(Number) : null;
  const { model: period } = await new PeriodModel({ id }).fetch({ silentError: true });
  const [closed, teamTotals] = await Promise.all([
    resolveReference('periodStatuses', PERIOD_STATUS.CLOSED),
    teamIds ? teamTotalsLoader(teamIds)([id]) : fetchSummaries([period], PERIOD_SUMMARIES).then(() => null),
  ]);
  const { collection } = await new Collection(PeriodStatusHistoryModel)
    .setFilter('period', id)
    .setFilter('periodStatus', String(closed.id))
    .list();
  const totals = teamTotals?.get(Number(id)) ?? {
    timeSheetCount: period.timeSheetCount,
    totalMinutes: period.totalMinutes,
  };

  return { period, totals, closing: collection.models[0] ?? null };
}

/** Équipes de la société, archivées comprises (une feuille garde l'équipe de sa soumission), par nom */
async function loadTeams(companyId: string): Promise<TeamModel[]> {
  // Statut des équipes (archivées), lu par son id
  await loadReferences('teamStatuses');
  const { collection } = await new Collection(TeamModel).setFilter('company', companyId).setSort('name').list();
  return collection.models;
}

/**
 * Récapitulatif mensuel d'une période de l'Historique (/history/{id}), vue du Dirigeant ou du manager : le mois, ses
 * compteurs (PeriodSummary ; le manager : ses équipes, PeriodTeamSummary) et sa date de clôture, puis le détail par
 * collaborateur (le manager : ses équipes) : une ligne par feuille soumise
 * (NOM Prénom, équipe figée à la soumission, total d'heures, statut), paginé côté API, filtrable par équipe et par nom
 * (l'en-tête reste celui de toute la période), précédé de la répartition par collaborateur (comme le Tableau de bord,
 * tout validé). Le Dirigeant exporte les feuilles filtrées (CSV sans ou avec détails, ZIP
 * des PDF : ExportButton). La société de la sidebar suit celle de la période.
 */
export function HistoryDetailPage() {
  const { id = '' } = useParams();
  const reviewer = reviewerOf(useCurrentCompany());
  const scope = reviewer.isDirector ? null : reviewer.managedTeamIds.map(String);
  const { data: recap, error, loaded } = useLoader(scope ? `${id}|${scope.join(',')}` : id, loadRecap);

  useFollowEntityCompany(recap?.period.companyId, '/history');
  const scopeKey = scope?.join(',') ?? '';
  const teamIds = useMemo(() => (scopeKey ? scopeKey.split(',').map(Number) : null), [scopeKey]);

  if (!recap) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Période introuvable." />;
  }

  const { period, totals, closing } = recap;

  return (
    <div className="space-y-6">
      <DetailHeader
        back={BACK}
        label="Période"
        title={period.monthLabel}
        subtitle={
          <p className="text-sm text-slate-500 mt-1">
            {scope ? 'Récapitulatif mensuel de vos équipes' : 'Récapitulatif mensuel, toutes équipes confondues'}
          </p>
        }
        actions={null}
      >
        <Value label="Total heures">
          <span className="text-2xl font-bold">{formatMinutes(totals.totalMinutes)}</span>
        </Value>
        <Value label="Demandes">
          <span className="text-2xl font-bold">{totals.timeSheetCount}</span>
        </Value>
        <Value label="Clôturée le">{closing ? closing.createdAt.format(DATE_TIME_FORMAT) : '–'}</Value>
      </DetailHeader>

      {/* Comme le Tableau de bord : période clôturée, tout est validé */}
      <CollaboratorBreakdown
        periodId={Number(period.id)}
        teamIds={teamIds}
        closed
        sheetPath={(sheetId) => `/history/${period.id}/sheets/${sheetId}`}
      />

      <PeriodSheets periodId={Number(period.id)} companyId={period.companyId} scope={scope} />
    </div>
  );
}

function PeriodSheets({
  periodId,
  companyId,
  scope,
}: {
  periodId: number;
  companyId: number;
  /** Équipes du manager (null : le Dirigeant, toutes) */
  scope: string[] | null;
}) {
  const list = useListControls({ filters: DEFAULT_PERIOD_SHEET_FILTERS, sort: DEFAULT_PERIOD_SHEET_SORT });
  const { filters, search, sort, page, itemsPerPage, changeFilters, changeSort } = list;
  // Sans les équipes (erreur), le filtre ne propose que « Toutes les équipes »
  const { data: companyTeams } = useLoader(String(companyId), loadTeams);
  const scopeKey = scope?.join(',') ?? '';
  const teams = scopeKey ? companyTeams?.filter((team) => scopeKey.split(',').includes(String(team.id))) : companyTeams;
  const statuses = useReferenceOptions('timeSheetStatuses');
  const timeSheetTypes = useReferenceOptions('timeSheetTypes');
  const countedStatusIds = useMemo(
    () =>
      statuses
        .filter((status) => status.reference !== TIME_SHEET_STATUS.DRAFT)
        .map((status) => status.id)
        .sort((a, b) => a - b),
    [statuses],
  );
  const request = useMemo(
    () =>
      buildPeriodSheetsRequest(
        periodId,
        countedStatusIds,
        { ...filters, search },
        sort,
        page,
        itemsPerPage,
        scopeKey ? scopeKey.split(',') : null,
      ),
    [periodId, countedStatusIds, filters, search, sort, page, itemsPerPage, scopeKey],
  );
  // Type des feuilles (absence) : lus par leur id dans les listes de référence (chargées une fois par session)
  useReferences('timeSheetTypes');
  const {
    models: sheets,
    totalItems,
    loading,
    error,
  } = usePagedList(TimeSheetModel, request, 0, {
    resolvables: RESOLVABLES,
    summary: TIME_SHEET_SUMMARIES,
  });
  const signatures = useSignatures(useMemo(() => sheets.map((sheet) => Number(sheet.id)), [sheets]));
  // Fiche employé : Paramétrage Dirigeant de la société sélectionnée
  const canOpenEmployee = canAccess('/employees/0', useAccessRoles());

  return (
    <CollapsiblePanel title="Détail par collaborateur" count={loading && sheets.length === 0 ? undefined : totalItems}>
      <ListToolbar
        search={
          <SearchInput
            label="Rechercher un collaborateur"
            value={filters.search}
            onChange={(value) => changeFilters({ search: value })}
          />
        }
      >
        <FilterSelect
          label="Filtrer par équipe"
          allLabel="Toutes les équipes"
          value={filters.team}
          onChange={(team) => changeFilters({ team })}
        >
          {teams?.map((team) => (
            <option key={team.id} value={String(team.id)}>
              {team.name}
              {team.teamStatusReference === TEAM_STATUS.ARCHIVED ? ' (archivée)' : ''}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Filtrer par type de feuille"
          allLabel="Tous les types"
          value={filters.timeSheetType}
          onChange={(timeSheetType) => changeFilters({ timeSheetType })}
        >
          {timeSheetTypes.map((type) => (
            <option key={type.id} value={String(type.id)}>
              {type.label}
            </option>
          ))}
        </FilterSelect>
        {/* Export des feuilles filtrées (toutes les pages) : le Dirigeant seul (cahier des charges, « admin ») */}
        {!scope && request && <ExportButton companyId={companyId} filters={exportFiltersOf(request)} />}
      </ListToolbar>

      <div className="overflow-x-auto">
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortableHeader
                className="px-6 py-4"
                label="Collaborateur"
                sortKey="name"
                sort={sort}
                onSort={changeSort}
              />
              <SortableHeader className="px-6 py-4" label="Équipe" sortKey="team" sort={sort} onSort={changeSort} />
              <SortableHeader
                className="px-6 py-4"
                label="Total heures"
                sortKey="total"
                sort={sort}
                onSort={changeSort}
              />
              {/* Signatures (TimeSheetSignature) : qui a signé, et quand */}
              <th className="px-6 py-4">Employé</th>
              <th className="px-6 py-4">Manager</th>
              <th className="px-6 py-4">Dirigeant</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sheets.map((sheet) => {
              const sheetSignatures = signatures?.get(Number(sheet.id));
              return (
                <tr key={sheet.id} className="hover:bg-slate-50/80 transition-colors" data-testid="period-sheet-row">
                  <PersonCell
                    to={canOpenEmployee ? `/employees/${sheet.memberId}` : undefined}
                    firstName={sheet.firstName}
                    lastName={sheet.lastName}
                    fullName={sheet.fullName}
                  />
                  <td className="px-6 py-4">
                    <TeamTag name={sheet.teamName} color={sheet.teamColor} />
                  </td>
                  <td className={clsx('px-6 py-4 font-bold', sheet.totalMinutes ? 'text-slate-900' : 'text-slate-400')}>
                    {/* Absence : rien n'a été déclaré, à ne pas confondre avec « 0h » (aucune heure, déclaré par l'employé) */}
                    {sheet.isAbsence ? <AbsenceBadge /> : formatMinutes(sheet.totalMinutes)}
                  </td>
                  <td className="px-6 py-4">
                    {/* Absence : validée sans l'employé, qui la signe ensuite */}
                    <SignatureCell
                      signature={sheetSignatures?.employee}
                      loading={!signatures}
                      pending={sheet.isAbsence ? ABSENCE_SIGNATURE_PENDING : undefined}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <SignatureCell signature={sheetSignatures?.manager} loading={!signatures} />
                  </td>
                  <td className="px-6 py-4">
                    <SignatureCell signature={sheetSignatures?.director} loading={!signatures} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end">
                      <RowViewLink
                        to={`/history/${periodId}/sheets/${sheet.id}`}
                        label={`Voir la feuille de ${sheet.fullName}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={!request || (loading && sheets.length === 0)}
          error={error}
          empty={
            !loading &&
            sheets.length === 0 &&
            (search || filters.team
              ? 'Aucune feuille ne correspond à ces critères.'
              : 'Aucune feuille soumise sur cette période.')
          }
        />
      </div>

      <Pagination {...list.pagination} totalItems={totalItems} label={['feuille', 'feuilles']} feminine />
    </CollapsiblePanel>
  );
}
